import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Globe, Phone, Mail, Star,
  Users, Building2, Calendar, Clock, TrendingUp,
  CheckCircle2, ExternalLink, Linkedin, Instagram,
  Facebook, ChevronRight, Award, FileText, Hash,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useCompany } from '@/hooks/useCompany';
import { useJobs, useGuestJobs } from '@/hooks/useJob';
import { cn } from '@/lib/utils';
import JobCard from '@/components/card/JobCard';
import { formatNumber } from '@/utils';

/* ─── TikTok icon (không có trong lucide) ─── */
function TiktokIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.93a8.16 8.16 0 004.77 1.52V7.01a4.85 4.85 0 01-1-.32z" />
    </svg>
  );
}

/* ─── Info row ─── */
function InfoRow({ icon, label, value, accent }: {
  icon: React.ReactNode; label: string; value?: string | number | null; accent?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-3 py-3 border-b last:border-0"
      style={{ borderColor: 'var(--border-c)' }}>
      <div className="flex items-center gap-2 shrink-0" style={{ color: 'var(--text-3)' }}>
        <span className="shrink-0">{icon}</span>
        <span className="text-xs">{label}</span>
      </div>
      <span className="text-xs font-semibold text-right"
        style={{ color: accent ? 'var(--brand)' : 'var(--text-1)' }}>
        {value}
      </span>
    </div>
  );
}

/* ─── Stat card ─── */
function StatCard({ icon, label, value, accent }: {
  icon: React.ReactNode; label: string; value: string | number; accent?: boolean;
}) {
  return (
    <div className="rounded-xl p-4 border text-center" style={{
      background: accent ? 'var(--brand-pale)' : 'var(--surface-1)',
      borderColor: accent ? 'rgba(249,115,22,0.2)' : 'var(--border-c)',
    }}>
      <div className="flex justify-center mb-2" style={{ color: accent ? 'var(--brand)' : 'var(--text-3)' }}>
        {icon}
      </div>
      <p className="font-black text-xl leading-none mb-1"
        style={{ color: accent ? 'var(--brand)' : 'var(--text-1)' }}>{value}</p>
      <p className="text-[10px] font-medium" style={{ color: 'var(--text-3)' }}>{label}</p>
    </div>
  );
}

/* ─── Social link ─── */
function SocialLink({ href, icon, label }: { href: string; icon: React.ReactNode; label: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors hover:border-orange-400 hover:text-orange-50"
      style={{ borderColor: 'var(--border-c)', color: 'var(--text-2)' }}>
      {icon} {label} <ExternalLink size={10} />
    </a>
  );
}



/* ─── Main Page ─── */
export default function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const { data: company, isLoading } = useCompany(id!);
  
  const isGuestOrNonAdmin = !user || user.role !== 'admin';
  const { data: jobsResponse } = isGuestOrNonAdmin 
    ? useGuestJobs({ companyId: id, approvalStatus: 'approved' }) 
    : useJobs({ companyId: id });
  const jobs = jobsResponse?.data || [];

  if (isLoading) return (
    <div className="flex items-center justify-center py-32">
      <div className="w-10 h-10 border-4 rounded-full animate-spin"
        style={{ borderColor: 'var(--brand-dim)', borderTopColor: 'var(--brand)' }} />
    </div>
  );

  if (!company) return (
    <div className="text-center py-24">
      <p className="text-5xl mb-4">🏢</p>
      <p className="font-bold text-xl mb-4" style={{ color: 'var(--text-1)' }}>
        Không tìm thấy công ty
      </p>
      <Link to="/cong-ty">
        <button className="btn-outline-brand px-5 py-2 rounded-xl text-sm font-semibold">
          ← Quay lại
        </button>
      </Link>
    </div>
  );

  const rating = parseFloat(company.rating || '0');

  return (
    <div className="max-w-5xl mx-auto animate-fade-in">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm mb-6" style={{ color: 'var(--text-3)' }}>
        <Link to="/cong-ty" className="flex items-center gap-1 hover:text-orange-500 transition-colors">
          <ArrowLeft size={14} /> Công ty
        </Link>
        <ChevronRight size={12} />
        <span className="truncate max-w-xs" style={{ color: 'var(--text-1)' }}>{company.name}</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">

        {/* ══ LEFT ══ */}
        <div className="lg:col-span-2 space-y-5">

          {/* Hero card */}
          <div className="rounded-2xl border p-6" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>

            {/* Logo + Name */}
            <div className="flex items-start gap-5 mb-6">
              <div className="w-20 h-20 rounded-2xl border overflow-hidden shrink-0 flex items-center justify-center"
                style={{ borderColor: 'var(--border-c)', background: 'var(--surface-2)' }}>
                {company.logoUrl ? (
                  <img src={company.logoUrl} alt={company.name}
                    className="w-full h-full object-contain p-2"
                    onError={e => {
                      (e.target as HTMLImageElement).src = '';
                      (e.target as HTMLImageElement).style.display = 'none';
                    }} />
                ) : (
                  <span className="text-3xl font-black" style={{ color: 'var(--brand)' }}>
                    {company?.name?.[0]}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-display font-black text-2xl leading-tight mb-1"
                  style={{ color: 'var(--text-1)' }}>
                  {company.name}
                </h1>
                <p className="text-sm mb-2" style={{ color: 'var(--text-3)' }}>{company.industry}</p>

                {/* Badges */}
                <div className="flex flex-wrap gap-2">
                  <span className="fnb-tag fnb-tag-brand text-xs">{company.businessSectorName}</span>
                  <span className="fnb-tag text-xs">{company.businessTypeName}</span>
                  <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full',
                    company.operatingStatus === 'active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-500')}>
                    {company.operatingStatus === 'active' ? '● Đang hoạt động' : '○ Tạm dừng'}
                  </span>
                  {company.approvalStatus === 'approved' && (
                    <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium"
                      style={{ background: '#dbeafe', borderColor: '#bfdbfe', color: '#1d4ed8' }}>
                      <CheckCircle2 size={9} /> Đã xác thực
                    </span>
                  )}
                </div>
              </div>

              {/* Rating pill */}
              <div className="shrink-0 flex flex-col items-center gap-1 px-4 py-3 rounded-2xl"
                style={{ background: 'var(--brand-pale)', border: '1px solid rgba(249,115,22,0.2)' }}>
                <div className="flex items-center gap-1" style={{ color: '#f59e0b' }}>
                  {[1, 2, 3, 4, 5].map(i => (
                    <Star key={i} size={12} fill={i <= Math.round(rating) ? 'currentColor' : 'none'} />
                  ))}
                </div>
                <p className="font-black text-xl leading-none" style={{ color: 'var(--brand)' }}>
                  {rating.toFixed(1)}
                </p>
                <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>
                  {formatNumber(company?.reviewCount || 0)} đánh giá
                </p>
              </div>
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard icon={<Building2 size={18} />} label="Chi nhánh" accent
                value={company?.numberOfBranches || 0} />
              <StatCard icon={<Users size={18} />} label="Nhân viên"
                value={company.size} />
              <StatCard icon={<Calendar size={18} />} label="Thành lập"
                value={company.foundedYear} />
              <StatCard icon={<Clock size={18} />} label="Giờ mở cửa"
                value={company.openingHours || '—'} />
            </div>
          </div>

          {/* Description */}
          {company.description && (
            <div className="rounded-2xl border p-6" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
              <h2 className="font-bold text-base mb-3 flex items-center gap-2"
                style={{ color: 'var(--text-1)' }}>
                <FileText size={16} style={{ color: 'var(--brand)' }} /> Giới thiệu công ty
              </h2>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>
                {company.description}
              </p>
            </div>
          )}

          {/* Business info */}
          <div className="rounded-2xl border p-6" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
            <h2 className="font-bold text-base mb-4 flex items-center gap-2"
              style={{ color: 'var(--text-1)' }}>
              <Building2 size={16} style={{ color: 'var(--brand)' }} /> Thông tin doanh nghiệp
            </h2>
            <div className="grid sm:grid-cols-2 gap-2  border rounded-xl overflow-hidden px-2"
              style={{ borderColor: 'var(--border-c)' }}>
              <InfoRow icon={<Hash size={13} />} label="Mã số thuế" value={company.taxCode} />
              <InfoRow icon={<Users size={13} />} label="Người đại diện" value={company.legalRepresentative} />
              <InfoRow icon={<Building2 size={13} />} label="Quy mô" value={`${company.size} nhân viên`} />
              <InfoRow icon={<Calendar size={13} />} label="Năm thành lập" value={String(company.foundedYear)} />
              <InfoRow icon={<TrendingUp size={13} />} label="Ngành" value={company.industry} />
              <InfoRow icon={<Award size={13} />} label="Số chi nhánh" value={`${company.numberOfBranches} chi nhánh`} accent />
              <InfoRow icon={<MapPin size={13} />} label="Địa chỉ" value={company.address} />
              <InfoRow icon={<Clock size={13} />} label="Giờ hoạt động" value={company.openingHours} />
            </div>
          </div>

          {/* Social links */}
          <div className="rounded-2xl border p-6" style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
            <h2 className="font-bold text-base mb-4 flex items-center gap-2"
              style={{ color: 'var(--text-1)' }}>
              <Globe size={16} style={{ color: 'var(--brand)' }} /> Mạng xã hội & Liên kết
            </h2>
            <div className="flex flex-wrap gap-2">
              {company.website && (
                <SocialLink href={company.website} icon={<Globe size={12} />} label="Website" />
              )}
              {company.linkedin && (
                <SocialLink href={company.linkedin} icon={<Linkedin size={12} />} label="LinkedIn" />
              )}
              {company.facebook && (
                <SocialLink href={company.facebook} icon={<Facebook size={12} />} label="Facebook" />
              )}
              {company.instagram && (
                <SocialLink href={company.instagram} icon={<Instagram size={12} />} label="Instagram" />
              )}
              {company.tiktok && (
                <SocialLink href={company.tiktok} icon={<TiktokIcon size={12} />} label="TikTok" />
              )}
            </div>
          </div>

          {/* Jobs section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-lg flex items-center gap-2" style={{ color: 'var(--text-1)' }}>
                <Users size={20} style={{ color: 'var(--brand)' }} /> Vị trí đang tuyển dụng
              </h2>
              {jobs.length > 0 && (
                <span className="text-sm font-medium text-gray-500">{jobs.length} tin đăng</span>
              )}
            </div>

            {jobs.length > 0 ? (
              <div className="grid  gap-4">
                {jobs.map(job => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed p-10 text-center"
                style={{ borderColor: 'var(--border-c)', background: 'var(--surface-1)' }}>
                <p className="text-sm" style={{ color: 'var(--text-3)' }}>
                  Hiện tại công ty chưa có vị trí nào đang tuyển dụng.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ══ RIGHT: Sidebar ══ */}
        <div className="space-y-4">

          {/* Contact card */}
          <div className="rounded-2xl border p-5 sticky top-24"
            style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
            <h3 className="font-bold text-base mb-4" style={{ color: 'var(--text-1)' }}>
              Liên hệ
            </h3>

            {/* Rating prominent */}
            <div className="rounded-xl p-3 mb-4 text-center"
              style={{ background: 'var(--brand-pale)', border: '1px solid rgba(249,115,22,0.2)' }}>
              <div className="flex justify-center gap-0.5 mb-1" style={{ color: '#f59e0b' }}>
                {[1, 2, 3, 4, 5].map(i => (
                  <Star key={i} size={14} fill={i <= Math.round(rating) ? 'currentColor' : 'none'} />
                ))}
              </div>
              <p className="font-black text-2xl" style={{ color: 'var(--brand)' }}>
                {rating.toFixed(1)}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                {formatNumber(company?.reviewCount || 0)} lượt đánh giá
              </p>
            </div>

            {/* Contact buttons */}
            <div className="space-y-2">
              {company.phone && (
                <a href={`tel:${company.phone}`}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white"
                  style={{ background: 'var(--brand)' }}>
                  <Phone size={14} /> {company.phone}
                </a>
              )}
              {/* {company.email && (
                <a href={`mailto:${company.email}`}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-colors hover:border-orange-400 hover:text-orange-500"
                  style={{ borderColor: 'var(--border-c)', color: 'var(--text-2)' }}>
                  <Mail size={14} /> {company.email}
                </a>
              )}
              {company.website && (
                <a href={company.website} target="_blank" rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-colors hover:border-orange-400 hover:text-orange-500"
                  style={{ borderColor: 'var(--border-c)', color: 'var(--text-2)' }}>
                  <Globe size={14} /> Website <ExternalLink size={11} />
                </a>
              )}
              {company.fax && (
                <p className="text-xs text-center" style={{ color: 'var(--text-3)' }}>
                  Fax: {company.fax}
                </p>
              )} */}
            </div>
          </div>

          {/* Quick info */}
          <div className="rounded-2xl border p-5"
            style={{ background: 'var(--surface-0)', borderColor: 'var(--border-c)' }}>
            <h3 className="font-bold text-sm mb-3" style={{ color: 'var(--text-1)' }}>
              Thông tin nhanh
            </h3>
            <div className="space-y-0">
              {[
                { icon: <Building2 size={13} />, label: 'Loại hình', value: company.businessTypeName },
                { icon: <TrendingUp size={13} />, label: 'Ngành', value: company.businessSectorName },
                { icon: <Users size={13} />, label: 'Quy mô', value: `${company.size} nhân viên` },
                { icon: <MapPin size={13} />, label: 'Địa chỉ', value: company.address },
                { icon: <Clock size={13} />, label: 'Giờ mở cửa', value: company.openingHours },
                { icon: <Calendar size={13} />, label: 'Thành lập', value: `Năm ${company.foundedYear}` },
                { icon: <Building2 size={13} />, label: 'Chi nhánh', value: `${company.numberOfBranches} cơ sở`, accent: true },
              ].filter(r => r.value).map(({ icon, label, value, accent }) => (
                <div key={label}
                  className="flex items-center justify-between gap-2 py-2 border-b last:border-0"
                  style={{ borderColor: 'var(--border-c)' }}>
                  <div className="flex items-center gap-1.5 shrink-0" style={{ color: 'var(--text-3)' }}>
                    {icon} <span className="text-xs">{label}</span>
                  </div>
                  <span className="text-xs font-semibold text-right"
                    style={{ color: accent ? 'var(--brand)' : 'var(--text-1)' }}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}